import React, { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { OpportunityRoomAsset } from '~/hooks/apis/offers/useGetOffer';

import ApplyInclusionDialog from './ApplyInclusionDialog';
import AssetLinkField from './AssetLinkField';
import {
  INCLUSION_APPLIES_OPTIONS,
  INCLUSION_FIELD_LABELS,
  INCLUSION_TYPE_OPTIONS,
  INCLUSION_VALID_FOR_OPTIONS,
} from './fields';
import { describeApplyTo, describeInclusionValue, getAvailableOccupancies, isApplyToValid } from './inclusionsModel';
import type { PackageRoomRow } from './inclusionsModel';
import type { InclusionDraft, InclusionValidFor } from './types';

interface Props {
  currency: string;
  draft: InclusionDraft;
  knownAssets: Array<OpportunityRoomAsset>;
  onChange: (changes: Partial<InclusionDraft>) => void;
  onRemove: () => void;
  nights: Array<number>;
  rooms: Array<PackageRoomRow>;
}

// Every field writes straight back to the draft on change; "Applies to" is the one field that
// opens a dialog.
export default function InclusionEditCard({ currency, draft, knownAssets, onChange, onRemove, nights, rooms }: Props) {
  const [applyToDialogOpen, setApplyToDialogOpen] = useState(false);
  const matchesAnyPackage = isApplyToValid(rooms, nights, draft.applyTo);

  const toggleValidFor = (option: InclusionValidFor) => {
    onChange({
      validFor: draft.validFor.includes(option)
        ? draft.validFor.filter((entry) => entry !== option)
        : [...draft.validFor, option],
    });
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <TextField
              label={INCLUSION_FIELD_LABELS.name}
              value={draft.name}
              onChange={(event) => onChange({ name: event.target.value })}
              size="small"
              required
              sx={{ minWidth: 180, flex: 2 }}
            />
            <AssetLinkField
              currency={currency}
              knownAssets={knownAssets}
              value={draft.assetLink}
              onChange={(assetLink) => onChange({ assetLink })}
            />
            <TextField
              size="small"
              label="Applies to"
              value={describeApplyTo(draft.applyTo, rooms)}
              error={!matchesAnyPackage}
              helperText={matchesAnyPackage ? undefined : 'Matches no package'}
              onClick={() => setApplyToDialogOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setApplyToDialogOpen(true);
                }
              }}
              InputProps={{ readOnly: true }}
              sx={{ minWidth: 160, flex: 1, cursor: 'pointer' }}
            />
            <TextField
              label={INCLUSION_FIELD_LABELS.prv}
              type="number"
              value={draft.prv ?? ''}
              onChange={(event) =>
                onChange({ prv: event.target.value === '' ? undefined : Number(event.target.value) })
              }
              size="small"
              helperText="Not sent to Salesforce"
              InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              sx={{ minWidth: 160, flex: 1 }}
            />
            <TextField
              select
              label={INCLUSION_FIELD_LABELS.type}
              value={draft.type ?? ''}
              onChange={(event) => onChange({ type: event.target.value || undefined })}
              size="small"
              sx={{ minWidth: 130, flex: 1 }}
            >
              {INCLUSION_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={INCLUSION_FIELD_LABELS.applies}
              value={draft.applies ?? ''}
              onChange={(event) =>
                onChange({ applies: (event.target.value || undefined) as InclusionDraft['applies'] })
              }
              size="small"
              sx={{ minWidth: 130, flex: 1 }}
            >
              {INCLUSION_APPLIES_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            <IconButton size="small" onClick={onRemove} aria-label="Remove inclusion" sx={{ mt: 0.5 }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="flex-start">
            <TextField
              label="Fine print"
              value={draft.description ?? ''}
              onChange={(event) => onChange({ description: event.target.value })}
              size="small"
              sx={{ minWidth: 240, flex: 2 }}
            />
            <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
              {INCLUSION_VALID_FOR_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      size="small"
                      checked={draft.validFor.includes(option)}
                      onChange={() => toggleValidFor(option)}
                    />
                  }
                  label={option}
                />
              ))}
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary" fontStyle={draft.prv ? undefined : 'italic'}>
            {describeInclusionValue({
              prv: draft.prv,
              type: draft.type,
              applies: draft.applies,
              leadNights: nights[0],
            })}
          </Typography>
        </Stack>
      </CardContent>

      <ApplyInclusionDialog
        open={applyToDialogOpen}
        value={draft.applyTo}
        rooms={rooms}
        occupancies={getAvailableOccupancies(rooms)}
        nights={nights}
        onClose={() => setApplyToDialogOpen(false)}
        onApply={(applyTo) => onChange({ applyTo })}
      />
    </Card>
  );
}
